import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HybridTabConfig, HYBRID_TAB_NAMES, HYBRID_TAB_DISPLAY_NAMES, HybridTabName } from '../../../generic-integrations/models/outgoing-events.model';

@Component({
	selector: 'cv-invision-outgoing-events-hybrid-tabs',
	templateUrl: './outgoing-events-hybrid-tabs.component.html',
	styleUrls: ['./outgoing-events-hybrid-tabs.component.scss']
})
export class OutgoingEventsHybridTabsComponent implements OnInit {
	@Input() config: HybridTabConfig;
	public form: FormGroup;
	public saving = false;
	public selectedTabIndex = 0;
	public eventNameOptions: { value: any; label: any; }[] = [];

	readonly HYBRID_TAB_NAMES = HYBRID_TAB_NAMES;
	readonly HYBRID_TAB_DISPLAY_NAMES = HYBRID_TAB_DISPLAY_NAMES;

	// Save the original form value to detect changes
	private originalFormValue: any;

	constructor(private fb: FormBuilder) { }

	ngOnInit(): void {
		this.form = this.fb.group({
			product: this.buildFormArray(this.config.hybridData.product),
			productHybrid: this.buildFormArray(this.config.hybridData.productHybrid),
			productNonHybrid: this.buildFormArray(this.config.hybridData.productNonHybrid)
		});

		this.loadEventNameOptions();

		// Store initial value snapshot for change detection
		this.originalFormValue = JSON.stringify(this.form.value);
	}

	buildFormArray(data: any[]): FormArray {
		return this.fb.array(
			data.map(item => this.fb.group({
				eventName: [item.eventName || null],
				postIf: [item.postIf || null],
				enabled: [item.enabled || false]
			}))
		);
	}

	getFormArray(tab: HybridTabName): FormArray {
		return this.form.get(tab) as FormArray;
	}

	/**
	 * Adds or removes a product form group from the specified tab's form array.
	 * @param type - The tab type.
	 * @param action - The action to perform: 'add' or 'delete'.
	 * @param index - Optional index used when deleting.
	 */
	onModifyProduct(type: HybridTabName, action: 'add' | 'delete', index?: number): void {
		const control = this.form.get(type) as FormArray;
		if (!control) return;

		if (action === 'add') {
			const defaultEventName = this.eventNameOptions?.[0]?.value ?? null;
    		const defaultPostIf = this.config?.optionsPostIf?.[0].value ?? null;

			control.push(this.fb.group({
				eventName: [defaultEventName, Validators.required],
				postIf: [defaultPostIf, Validators.required],
				enabled: [true]
			  }));
		} else if (action === 'delete' && typeof index === 'number') {
			control.removeAt(index);
		}
	}

	/**
	 * Loads eventNameOptions from config.hybridData and config.cmpRelation.
	 */
	private loadEventNameOptions(): void {
		const hybridData = this.config.hybridData;
		const cmpRelationArray = this.config.cmpRelation || [];

		// Flatten and extract all event names from hybridData arrays
		const allEventItems = [
			...(hybridData.product || []),
			...(hybridData.productHybrid || []),
			...(hybridData.productNonHybrid || [])
		];

		const uniqueEventNames = [
			...new Set(allEventItems.map(item => item.eventName).filter(name => !!name))
		];

		// Convert cmpRelation array to map for easier lookup
		const cmpRelationMap: Record<string, string> = Object.fromEntries(
			cmpRelationArray.map(({ value, label }) => [value, label])
		);

		// Build final eventNameOptions
		this.eventNameOptions = uniqueEventNames.map(name => ({
			value: name,
			label: cmpRelationMap[name] || name
		}));
	}

	getEnabledControl(tab: HybridTabName, index: number): FormControl {
		return this.getFormArray(tab).at(index).get('enabled') as FormControl;
	}

	isFormCompletelyValid(): boolean {
		return this.form.valid;
	}

	getCurrentPayload() {
		return this.form.value;
	}

	/** 
	 * Checks if form can be saved: valid and has changes compared to original.
	 */
	canSave(): boolean {
		return this.form.valid && JSON.stringify(this.form.value) !== this.originalFormValue;
	}

	/**
	 * Submit handler for the form.
	 * Marks controls touched if invalid, else triggers save logic.
	 */
	submitForm(): void {
		if (!this.canSave()) {
			this.form.markAllAsTouched();
			return;
		}
		this.saving = true;
	}
}
